/**
 * Sparkline SVG mínimo — sin libs externas. Soporta arrays de 0..N puntos
 * (gaps con null permitidos). Color de la línea es el `current` status.
 * El último punto se dibuja con un círculo destacado.
 */
import type { EvalStatus } from "@axorial/shared";

export interface SparkPoint {
  period: string;
  value: number | null;
}

export function MetricSparkline({
  points,
  width = 96,
  height = 28,
  status = "unknown",
  className,
}: {
  points: SparkPoint[];
  width?: number;
  height?: number;
  status?: EvalStatus;
  className?: string;
}) {
  const valid = points.filter((p): p is { period: string; value: number } => p.value != null);
  if (valid.length === 0) {
    return (
      <svg width={width} height={height} className={className}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          strokeOpacity={0.2}
          strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const min = Math.min(...valid.map((p) => p.value));
  const max = Math.max(...valid.map((p) => p.value));
  const span = max - min || 1;
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;

  const norm = (v: number) => height - ((v - min) / span) * height * 0.85 - height * 0.075;

  // Segments por gaps (null) — generamos polylines independientes.
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((p, i) => {
    if (p.value == null) {
      if (current.length > 0) segments.push(current.join(" "));
      current = [];
      return;
    }
    current.push(`${i * stepX},${norm(p.value)}`);
  });
  if (current.length > 0) segments.push(current.join(" "));

  const stroke =
    status === "good"
      ? "var(--color-success, currentColor)"
      : status === "improvable"
        ? "var(--color-warning, currentColor)"
        : status === "bad"
          ? "var(--color-error, currentColor)"
          : "currentColor";

  const lastValid = [...valid].pop()!;
  const lastIdx = points.findIndex((p) => p.period === lastValid.period);

  return (
    <svg width={width} height={height} className={className} aria-hidden>
      {segments.map((s, i) => (
        <polyline
          key={i}
          points={s}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {lastIdx >= 0 && (
        <circle cx={lastIdx * stepX} cy={norm(lastValid.value)} r={2.5} fill={stroke} />
      )}
    </svg>
  );
}
